import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Award, Lock, CheckCircle, Star, Trophy, Sparkles, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const translations = {
  it: {
    title: 'Passaporto Digitale',
    subtitle: 'Colleziona badge esplorando Paestum',
    level: 'Livello',
    points: 'punti',
    nextLevel: 'Prossimo livello',
    myBadges: 'I Miei Badge',
    availableBadges: 'Badge Disponibili',
    unlocked: 'Sbloccato',
    locked: 'Bloccato',
    unlock: 'Sblocca',
    sharePassport: 'Condividi',
    noBadges: 'Non hai ancora badge. Esplora Paestum per sbloccarli!',
    loginRequired: 'Accedi per vedere il tuo passaporto'
  },
  en: {
    title: 'Digital Passport',
    subtitle: 'Collect badges by exploring Paestum',
    level: 'Level',
    points: 'points',
    nextLevel: 'Next level',
    myBadges: 'My Badges',
    availableBadges: 'Available Badges',
    unlocked: 'Unlocked',
    locked: 'Locked',
    unlock: 'Unlock',
    sharePassport: 'Share',
    noBadges: 'No badges yet. Explore Paestum to unlock them!',
    loginRequired: 'Login to see your passport'
  }
};

export default function PassportPage() {
  const { token, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const t = translations[language] || translations.it;
  
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPassport();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  const fetchPassport = async () => {
    try {
      const response = await fetch(`${API}/api/passport`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPassport(data);
      }
    } catch (error) {
      console.error('Error fetching passport:', error);
    } finally {
      setLoading(false);
    }
  };

  const unlockBadge = async (badgeId) => {
    setUnlocking(badgeId);
    try {
      const response = await fetch(`${API}/api/passport/unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ badge_id: badgeId })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchPassport(); // Refresh passport
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setUnlocking(null);
    }
  };

  const sharePassport = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Il mio Passaporto di Paestum',
        text: `Ho sbloccato ${passport?.badges?.length || 0} badge a La Maisonette di Paestum! 🏛️`,
        url: window.location.href
      });
    } else {
      toast.info('Condivisione non supportata su questo dispositivo');
    }
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 1: return 'from-gray-400 to-gray-500';
      case 2: return 'from-green-400 to-green-600';
      case 3: return 'from-blue-400 to-blue-600';
      case 4: return 'from-purple-400 to-purple-600';
      case 5: return 'from-yellow-400 to-amber-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A059]" />
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Award className="w-16 h-16 text-[#C5A059] mx-auto mb-4" />
          <h1 className="font-cinzel text-2xl text-[#1A202C] mb-4">{t.loginRequired}</h1>
          <Button onClick={() => window.location.href = '/login'} className="bg-[#C5A059]">
            Login
          </Button>
        </div>
      </Layout>
    );
  }

  const unlockedBadgeIds = passport?.badges?.map(b => b.id) || [];
  const availableBadges = passport?.all_badges?.filter(b => !unlockedBadgeIds.includes(b.id)) || [];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F9F9F7] to-[#EAE5DC]">
        {/* Header with Level */}
        <div className="bg-[#1A202C] py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Passport Visual */}
              <div className="relative">
                <div className={`w-28 h-36 rounded-lg bg-gradient-to-br ${getLevelColor(passport?.level || 1)} shadow-xl flex flex-col items-center justify-center text-white p-3`}>
                  <Trophy className="w-8 h-8 mb-2" />
                  <span className="text-xs uppercase tracking-wider opacity-80">Paestum</span>
                  <span className="font-cinzel text-lg font-bold">Passport</span>
                </div>
                <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-[#C5A059] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                  {passport?.level || 1}
                </div>
              </div>

              {/* User Info */}
              <div className="text-center md:text-left flex-1">
                <p className="text-[#C5A059] text-sm uppercase tracking-wider mb-1">{t.title}</p>
                <h1 className="font-cinzel text-2xl text-white mb-2">
                  {passport?.guest_name || 'Viaggiatore'}
                </h1>
                <p className="text-gray-400 mb-3">
                  {language === 'en' ? passport?.level_name?.[1] : passport?.level_name?.[0]}
                </p>
                
                {/* Progress Bar */}
                <div className="bg-white/10 rounded-full h-3 w-full max-w-xs mx-auto md:mx-0 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#C5A059] to-[#E6C985] transition-all duration-500"
                    style={{ width: `${Math.min((passport?.total_points || 0) / (passport?.next_level_points || 100) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {passport?.total_points || 0} / {passport?.next_level_points || 50} {t.points} - {t.nextLevel} {(passport?.level || 1) + 1}
                </p>
              </div>

              {/* Share Button */}
              <Button
                onClick={sharePassport}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t.sharePassport}
              </Button>
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Unlocked Badges */}
          <div className="mb-10">
            <h2 className="font-cinzel text-xl text-[#1A202C] mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#C5A059]" />
              {t.myBadges} ({passport?.badges?.length || 0})
            </h2>
            
            {passport?.badges?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {passport.badges.map((badge, idx) => (
                  <Card key={idx} className="bg-white border-[#C5A059]/30 border-2 overflow-hidden group hover:shadow-lg transition-all">
                    <CardContent className="p-4 text-center">
                      <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-[#C5A059]/20 to-[#C5A059]/5 rounded-full flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">
                        {badge.icon}
                      </div>
                      <h3 className="font-cinzel text-sm text-[#1A202C] mb-1">
                        {language === 'en' ? badge.name_en : badge.name}
                      </h3>
                      <p className="text-xs text-gray-500 mb-2">
                        {language === 'en' ? badge.description_en : badge.description}
                      </p>
                      <div className="flex items-center justify-center gap-1 text-[#C5A059]">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">+{badge.points} pts</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t.noBadges}</p>
              </div>
            )}
          </div>

          {/* Available Badges */}
          <div>
            <h2 className="font-cinzel text-xl text-[#1A202C] mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-gray-400" />
              {t.availableBadges} ({availableBadges.length})
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {availableBadges.map((badge, idx) => (
                <Card key={idx} className="bg-gray-50 border-gray-200 overflow-hidden opacity-75 hover:opacity-100 transition-all">
                  <CardContent className="p-4 text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gray-200 rounded-full flex items-center justify-center text-3xl grayscale">
                      {badge.icon}
                    </div>
                    <h3 className="font-cinzel text-sm text-gray-600 mb-1">
                      {language === 'en' ? badge.name_en : badge.name}
                    </h3>
                    <p className="text-xs text-gray-400 mb-3">
                      {language === 'en' ? badge.description_en : badge.description}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => unlockBadge(badge.id)}
                      disabled={unlocking === badge.id}
                      className="text-xs border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                    >
                      {unlocking === badge.id ? (
                        <span className="animate-spin">⏳</span>
                      ) : (
                        <>
                          <Star className="w-3 h-3 mr-1" />
                          {t.unlock} (+{badge.points})
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
